import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  useTheme,
  useMediaQuery,
  Skeleton,
  Avatar,
  Collapse,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  Add as AddIcon,
  Groups as GroupsIcon,
  ExpandMore as ExpandMoreIcon,
  PlayCircle as PlayCircleIcon,
} from "@mui/icons-material";
import VideoPlayerModal from "../VideoPlayerModal";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";
import { ActionModal, SearchField } from "../shared";
import { lightTheme } from "../../styles/theme";
import { useCircleData, useUserCircles } from "../../hooks/useCircleData";

interface ManageUserCirclesProps {
  user: {
    id: string;
    name: string;
    email: string;
    phoneNumber?: string;
  };
  /** Full-width page or embedded inside Family tabs on User Home. */
  layout?: "page" | "embedded";
  onCirclesChanged?: () => void;
  /** When provided, skips internal useUserCircles fetch (shared from parent). */
  userCirclesData?: import("../../hooks/useCircleData").UserCirclesQueryResult;
}

// Avatar utilities are now imported from shared utils

export const ManageUserCircles = React.memo<ManageUserCirclesProps>(
  ({
    user,
    layout = "page",
    onCirclesChanged,
    userCirclesData,
  }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isEmbedded = layout === "embedded";
    const isPageLayout = true;
    const { token, user: authUser } = useAuth();

    // UI state
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [addingCircleId, setAddingCircleId] = useState<string | null>(null);
    const [removingCircleId, setRemovingCircleId] = useState<string | null>(
      null
    );
    const [searchQuery, setSearchQuery] = useState("");
    const [inCirclesSearchQuery, setInCirclesSearchQuery] = useState("");
    const [desktopTab, setDesktopTab] = useState<"in-circles" | "can-add">(
      "in-circles"
    );
    const [isCurrentSubcirclesOpen, setIsCurrentSubcirclesOpen] = useState(true);
    const [aliasDrafts, setAliasDrafts] = useState<Record<string, string>>({});
    const [savingAliasCircleId, setSavingAliasCircleId] = useState<string | null>(
      null
    );
    const aliasDraftsRef = useRef<Record<string, string>>({});
    const savedAliasByCircleRef = useRef<Record<string, string>>({});
    const aliasDebounceRef = useRef<
      Record<string, ReturnType<typeof setTimeout>>
    >({});
    const [tutorialVideo, setTutorialVideo] = useState<{
      videoPath: string;
      title: string;
    } | null>(null);
    const [legacyAccessOption, setLegacyAccessOption] = useState<
      "none" | "7days" | "all"
    >("7days");
    /** Circle chosen via Add — confirm historical access before API call. */
    const [circlePendingAdd, setCirclePendingAdd] = useState<{
      circleId: string;
      name: string;
    } | null>(null);

    aliasDraftsRef.current = aliasDrafts;

    useEffect(() => {
      setIsCurrentSubcirclesOpen(true);
    }, [user.id]);

    // Use custom hooks for data management
    const { allCircles, isLoading: allCirclesLoading } = useCircleData();
    const internalUserCircles = useUserCircles(user.id, !userCirclesData);
    const {
      userCircles,
      isLoading: userCirclesLoading,
      error: userCirclesLoadError,
      refetch: refetchUserCircles,
    } = userCirclesData ?? internalUserCircles;

    const safeUserCircles = useMemo(
      () => (Array.isArray(userCircles) ? userCircles : []),
      [userCircles]
    );
    const hasOrgSubcircles = allCircles.some(
      (circle) => circle.isSubCircle === true
    );

    const isLoading = allCirclesLoading || userCirclesLoading;
    const showInitialLoadingSkeleton =
      isLoading &&
      safeUserCircles.length === 0 &&
      allCircles.length === 0;

    const requestAddToCircle = (circle: { circleId: string; name: string }) => {
      setError(null);
      setLegacyAccessOption("7days");
      setCirclePendingAdd(circle);
    };

    // Add user to circle (after historical-access confirmation)
    const confirmAddPendingCircle = async () => {
      const pending = circlePendingAdd;
      if (!pending || !token || !authUser?.schoolId) {
        if (!token || !authUser?.schoolId) {
          setError("Authentication required. Please log in again.");
        }
        return;
      }

      setAddingCircleId(pending.circleId);
      setError(null);
      setSuccess(null);

      try {
        await api.circles.addUserToSubCircle(
          {
            email: user.email,
            phoneNumber: user.phoneNumber,
            subcircleId: pending.circleId,
            schoolId: authUser.schoolId,
            accessLegacyMessages: legacyAccessOption === "all",
            legacyAccessDays: legacyAccessOption === "7days" ? 7 : undefined,
          },
          token
        );

        setCirclePendingAdd(null);
        setSuccess(`Successfully added ${user.name} to ${pending.name}`);

        // Refresh user's circles
        await refetchUserCircles();
        onCirclesChanged?.();

        setTimeout(() => setSuccess(null), 3000);
      } catch (err: any) {
        setError(
          err.message || "Failed to add user to circle. Please try again."
        );
      } finally {
        setAddingCircleId(null);
      }
    };

    // Remove user from circle (subcircles only — not the main organization circle)
    const handleRemoveFromCircle = async (circleId: string) => {
      if (!token || !authUser?.schoolId) {
        setError("Authentication required. Please log in again.");
        return;
      }

      const circleMeta = allCircles.find((c) => c.circleId === circleId);
      if (!circleMeta || circleMeta.isSubCircle !== true) {
        setError(
          "Users cannot be removed from the main organization circle on this screen. Remove them from a subcircle instead."
        );
        return;
      }

      setRemovingCircleId(circleId);
      setError(null);
      setSuccess(null);

      try {
        await api.circles.removeUserFromSubCircle(
          {
            userId: user.id,
            subcircleId: circleId,
            schoolId: authUser?.schoolId,
          },
          token
        );

        setSuccess(`Successfully removed ${user.name} from the circle`);

        // Refresh user's circles
        await refetchUserCircles();
        onCirclesChanged?.();

        setTimeout(() => setSuccess(null), 3000);
      } catch (err: any) {
        setError(
          err.message || "Failed to remove user from circle. Please try again."
        );
      } finally {
        setRemovingCircleId(null);
      }
    };

    const handleAliasDraftChange = (circleId: string, value: string) => {
      setAliasDrafts((prev) => ({
        ...prev,
        [circleId]: value,
      }));
    };

    const persistCircleAlias = useCallback(
      async (circleId: string) => {
        if (!token || !authUser?.schoolId) return;

        const raw = aliasDraftsRef.current[circleId] ?? "";
        const trimmed = raw.trim();
        if (trimmed.length > 60) {
          setError("Alias must be 60 characters or less.");
          return;
        }

        const lastSaved = savedAliasByCircleRef.current[circleId] ?? "";
        if (trimmed === lastSaved) return;

        setSavingAliasCircleId(circleId);
        setError(null);

        try {
          await api.circles.updateUserAliasInSubCircle(
            {
              userId: user.id,
              subcircleId: circleId,
              schoolId: authUser.schoolId,
              alias: trimmed,
            },
            token
          );

          savedAliasByCircleRef.current[circleId] = trimmed;
          await refetchUserCircles();
        } catch (err: any) {
          setError(err.message || "Failed to save alias. Please try again.");
        } finally {
          setSavingAliasCircleId(null);
        }
      },
      [token, authUser?.schoolId, user.id, refetchUserCircles]
    );

    const flushAliasSave = useCallback(
      (circleId: string) => {
        const t = aliasDebounceRef.current[circleId];
        if (t) {
          clearTimeout(t);
          delete aliasDebounceRef.current[circleId];
        }
        void persistCircleAlias(circleId);
      },
      [persistCircleAlias]
    );

    const scheduleAliasSave = useCallback(
      (circleId: string) => {
        if (aliasDebounceRef.current[circleId]) {
          clearTimeout(aliasDebounceRef.current[circleId]);
        }
        aliasDebounceRef.current[circleId] = setTimeout(() => {
          delete aliasDebounceRef.current[circleId];
          void persistCircleAlias(circleId);
        }, 700);
      },
      [persistCircleAlias]
    );

    // Memoized calculations for performance
    const enrichedUserCircles = useMemo(() => {
      return safeUserCircles.map((userCircle) => {
        const fullCircleData = allCircles.find(
          (circle) => circle.circleId === userCircle.circleId
        );
        return {
          ...userCircle,
          logo: fullCircleData?.logo,
          isSubCircle: fullCircleData?.isSubCircle,
        };
      });
    }, [safeUserCircles, allCircles]);

    useEffect(() => {
      setAliasDrafts((prev) => {
        const next = { ...prev };
        for (const circle of safeUserCircles) {
          if (next[circle.circleId] === undefined) {
            const a = circle.alias || "";
            next[circle.circleId] = a;
            savedAliasByCircleRef.current[circle.circleId] = a.trim();
          }
        }
        return next;
      });
    }, [safeUserCircles]);

    const filteredEnrichedUserCircles = useMemo(() => {
      if (!inCirclesSearchQuery.trim()) return enrichedUserCircles;
      const query = inCirclesSearchQuery.toLowerCase();
      return enrichedUserCircles.filter(
        (circle) =>
          circle.name.toLowerCase().includes(query) ||
          (circle.alias && circle.alias.toLowerCase().includes(query))
      );
    }, [enrichedUserCircles, inCirclesSearchQuery]);

    // Optimize filtering with Set-based lookup
    const userCircleIds = useMemo(() => {
      return new Set(safeUserCircles.map((circle) => circle.circleId));
    }, [safeUserCircles]);

    const availableCircles = useMemo(() => {
      return allCircles.filter(
        (circle) =>
          !userCircleIds.has(circle.circleId) && circle.isSubCircle === true
      );
    }, [allCircles, userCircleIds]);

    const filteredAvailableCircles = useMemo(() => {
      if (!searchQuery.trim()) return availableCircles;
      const query = searchQuery.toLowerCase();
      return availableCircles.filter(
        (circle) =>
          circle.name.toLowerCase().includes(query) ||
          circle.description?.toLowerCase().includes(query)
      );
    }, [availableCircles, searchQuery]);

    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const circlesPanel = (
        <Box sx={{ p: { xs: 0, sm: 0 } }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          )}
          {userCirclesLoadError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {userCirclesLoadError}
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isPageLayout
                ? "1fr"
                : { xs: "1fr", md: "1fr 1fr" },
              gap: { xs: 2, md: 3 },
              alignItems: "start",
              minWidth: 0,
            }}
          >
            {(!isPageLayout || desktopTab === "in-circles") && (
            <Box sx={{ minWidth: 0, overflow: "hidden" }}>
              <Box
                sx={{
                  px: isPageLayout ? { xs: 1.5, sm: isEmbedded ? 2 : 3 } : 0,
                  pt: isPageLayout ? (isEmbedded ? 0 : 2) : 0,
                  pb: isPageLayout ? (isEmbedded ? 1.5 : 0) : 0,
                }}
              >
              {!isPageLayout && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>
                  {`Current Subcircles (${enrichedUserCircles.length})`}
                </Typography>
                {isMobile && (
                  <IconButton
                    size="small"
                    onClick={() => setIsCurrentSubcirclesOpen((prev) => !prev)}
                    aria-label={
                      isCurrentSubcirclesOpen
                        ? "Collapse current subcircles"
                        : "Expand current subcircles"
                    }
                    sx={{
                      color: "text.secondary",
                      transform: isCurrentSubcirclesOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                )}
              </Box>
              )}

              <Collapse
                in={isPageLayout || !isMobile || isCurrentSubcirclesOpen}
                timeout="auto"
                unmountOnExit
              >
              {showInitialLoadingSkeleton ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {[1, 2, 3].map((i) => (
                    <Paper
                      key={i}
                      elevation={0}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: { xs: 2, sm: 2.5 },
                        minHeight: 80,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          flex: 1,
                          width: "100%",
                        }}
                      >
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <Skeleton variant="text" width="55%" height={20} />
                            <Skeleton variant="text" width={72} height={16} />
                          </Box>
                          <Skeleton variant="text" width="40%" height={16} />
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1.5,
                              mt: 1.25,
                              width: "100%",
                              minWidth: 0,
                              flexDirection: { xs: "column", sm: "row" },
                            }}
                          >
                            <Skeleton
                              variant="rectangular"
                              height={40}
                              sx={{
                                alignSelf: { xs: "stretch", sm: "auto" },
                                width: { xs: "100%", sm: 260 },
                                maxWidth: 280,
                                borderRadius: 1.5,
                              }}
                            />
                            <Skeleton
                              variant="rectangular"
                              width={80}
                              height={32}
                              sx={{
                                borderRadius: 1,
                                flexShrink: 0,
                                alignSelf: { xs: "flex-end", sm: "auto" },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : safeUserCircles.length === 0 ? (
                <Box
                  sx={{
                    p: isEmbedded ? 2 : 4,
                    textAlign: "center",
                    color: "text.secondary",
                  }}
                >
                  <GroupsIcon
                    sx={{
                      fontSize: isEmbedded ? 32 : 40,
                      mb: isEmbedded ? 1 : 2,
                      opacity: 0.3,
                      color: "text.secondary",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: (theme) => theme.typography.body2.fontSize,
                      opacity: 0.7,
                    }}
                  >
                    Not a member of any circles yet
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {filteredEnrichedUserCircles.map((circle) => (
                    <Paper
                      key={circle.circleId}
                      elevation={0}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        p: isEmbedded
                          ? { xs: 1.25, sm: 1.5 }
                          : { xs: 1.5, sm: 2 },
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: isEmbedded ? 1.5 : 2,
                          width: "100%",
                          minWidth: 0,
                        }}
                      >
                        <Avatar
                          src={
                            circle.logo &&
                            circle.logo.trim() !== "" &&
                            !circle.logo.includes("placehold.co")
                              ? circle.logo
                              : "/placeholder.jpg"
                          }
                          sx={{
                            width: isEmbedded ? 36 : 40,
                            height: isEmbedded ? 36 : 40,
                          }}
                        />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 1,
                              mb: circle.description || circle.isSubCircle ? 0.25 : 0,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: "0.9375rem", sm: "0.875rem" },
                                minWidth: 0,
                                lineHeight: 1.35,
                              }}
                            >
                              {circle.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: "0.75rem",
                                flexShrink: 0,
                                whiteSpace: "nowrap",
                                pt: 0.15,
                                textAlign: "right",
                              }}
                            >
                              {circle.joinedAt
                                ? formatDate(circle.joinedAt)
                                : "Recently"}
                            </Typography>
                          </Box>
                          {circle.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: "0.8125rem", sm: "0.8rem" },
                                lineHeight: 1.35,
                                display: "-webkit-box",
                                WebkitLineClamp: isEmbedded ? 1 : { xs: 3, sm: "unset" },
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {circle.description}
                            </Typography>
                          )}
                          {circle.isSubCircle === true && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                flexWrap: "wrap",
                                gap: 1,
                                mt: isEmbedded ? 0.75 : 1,
                                width: "100%",
                                minWidth: 0,
                              }}
                            >
                              <TextField
                                size="small"
                                sx={{
                                  flex: { xs: "1 1 100%", sm: "0 1 260px" },
                                  width: { xs: "100%", sm: 260 },
                                  maxWidth: "100%",
                                }}
                                placeholder={`How you appear in ${circle.name} (optional)`}
                                value={aliasDrafts[circle.circleId] ?? ""}
                                onChange={(e) => {
                                  handleAliasDraftChange(
                                    circle.circleId,
                                    e.target.value
                                  );
                                  scheduleAliasSave(circle.circleId);
                                }}
                                onBlur={() =>
                                  flushAliasSave(circle.circleId)
                                }
                                inputProps={{ maxLength: 60 }}
                                InputProps={{
                                  sx: {
                                    borderRadius: 1.5,
                                    bgcolor: "background.paper",
                                  },
                                  endAdornment:
                                    savingAliasCircleId === circle.circleId ? (
                                      <InputAdornment position="end">
                                        <CircularProgress
                                          color="inherit"
                                          size={16}
                                          thickness={5}
                                        />
                                      </InputAdornment>
                                    ) : undefined,
                                }}
                              />
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() =>
                                  handleRemoveFromCircle(circle.circleId)
                                }
                                disabled={removingCircleId === circle.circleId}
                                sx={{
                                  flexShrink: 0,
                                  textTransform: "none",
                                  minHeight: 36,
                                  minWidth: 88,
                                  px: 1.5,
                                }}
                              >
                                {removingCircleId === circle.circleId
                                  ? "Removing..."
                                  : "Remove"}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
              </Collapse>
              </Box>
            </Box>
            )}

            {(!isPageLayout || desktopTab === "can-add") && (
            <Box sx={{ minWidth: 0, overflow: "hidden" }}>
              <Box
                sx={{
                  px: isPageLayout ? { xs: 1.5, sm: isEmbedded ? 2 : 3 } : 0,
                  pb: isPageLayout ? (isEmbedded ? 1.5 : 3) : 0,
                  pt: isPageLayout ? (isEmbedded ? 0 : 2) : 0,
                }}
              >
              {!isPageLayout ? (
              <Typography
                variant="h6"
                sx={{ mb: 2, color: theme.palette.secondary.main }}
              >
                {`Available Subcircles (${filteredAvailableCircles.length})`}
              </Typography>
              ) : null}

              {!isPageLayout && (
              <Box sx={{ mb: 2 }}>
                <SearchField
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search subcircles by name or description..."
                />
              </Box>
              )}

              {filteredAvailableCircles.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: "center",
                    color: "text.secondary",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: (theme) => theme.typography.body2.fontSize,
                      opacity: 0.7,
                    }}
                  >
                    {searchQuery
                      ? "No subcircles found matching your search."
                      : hasOrgSubcircles
                        ? "No available subcircles to join"
                        : "Create a subcircle first, then add users to it from here."}
                  </Typography>
                  {!searchQuery && !hasOrgSubcircles && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.75,
                        mt: 1.5,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        How to create a subcircle
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label="Play how to create a subcircle tutorial"
                        onClick={() =>
                          setTutorialVideo({
                            videoPath: "/videos/createcircle.webm",
                            title: "How to Create a Circle",
                          })
                        }
                        sx={{
                          color: "primary.main",
                          "&:hover": { color: "primary.dark" },
                        }}
                      >
                        <PlayCircleIcon sx={{ fontSize: 28 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {filteredAvailableCircles.map((circle) => (
                    <Paper
                      key={circle.circleId}
                      elevation={0}
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "flex-start", sm: "center" },
                        p: isEmbedded
                          ? { xs: 1.25, sm: 1.5 }
                          : { xs: 1.5, sm: 2.5 },
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        gap: isEmbedded ? 1.25 : { xs: 2, sm: 2 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flex: 1,
                          width: { xs: "100%", sm: "auto" },
                        }}
                      >
                        <Avatar
                          src={
                            circle.logo &&
                            circle.logo.trim() !== "" &&
                            !circle.logo.includes("placehold.co")
                              ? circle.logo
                              : "/placeholder.jpg"
                          }
                          sx={{
                            width: 40,
                            height: 40,
                          }}
                        />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: "0.9375rem", sm: "0.875rem" },
                              mb: 0.5,
                            }}
                          >
                            {circle.name}
                          </Typography>
                          {circle.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: "0.8125rem", sm: "0.8rem" },
                                display: "-webkit-box",
                                WebkitLineClamp: { xs: 3, sm: "unset" },
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {circle.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: { xs: 1, sm: 2 },
                          width: { xs: "100%", sm: "auto" },
                          justifyContent: {
                            xs: "space-between",
                            sm: "flex-end",
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.75rem" } }}
                        >
                          {circle.isPublic ? "Public Circle" : "Private Circle"}
                        </Typography>
                        {isMobile ? (
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() =>
                              requestAddToCircle({
                                circleId: circle.circleId,
                                name: circle.name,
                              })
                            }
                            disabled={
                              !!addingCircleId || !!circlePendingAdd
                            }
                            aria-label={`Add ${circle.name}`}
                            sx={{
                              p: 0.5,
                              "&.Mui-disabled": {
                                color: "action.disabled",
                              },
                            }}
                          >
                            {addingCircleId === circle.circleId ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <AddIcon fontSize="small" />
                            )}
                          </IconButton>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() =>
                              requestAddToCircle({
                                circleId: circle.circleId,
                                name: circle.name,
                              })
                            }
                            disabled={
                              !!addingCircleId || !!circlePendingAdd
                            }
                            sx={{
                              minWidth: { xs: 100, sm: 100 },
                              fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                            }}
                          >
                            {addingCircleId === circle.circleId
                              ? "Adding..."
                              : "Add"}
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
              </Box>
            </Box>
            )}
          </Box>
        </Box>
    );

    return (
      <>
      <Paper
          elevation={0}
          sx={{
            mb: { xs: 2, md: 0 },
            border: isEmbedded
              ? "none"
              : { xs: "none", md: "1px solid" },
            borderColor: { md: "divider" },
            borderRadius: isEmbedded ? 0 : { xs: 0, md: 2 },
            overflow: { xs: "visible", md: "hidden" },
            backgroundColor: {
              xs: "transparent",
              md: isEmbedded ? "transparent" : "background.paper",
            },
          }}
        >
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              backgroundColor: isEmbedded ? "transparent" : "background.paper",
              borderRadius: isEmbedded ? 0 : { xs: 2, md: 0 },
              px: { xs: 1, md: isEmbedded ? 2 : 0 },
            }}
          >
            <Tabs
              value={desktopTab}
              onChange={(_, next) => setDesktopTab(next)}
              variant={isMobile ? "fullWidth" : "scrollable"}
              scrollButtons={isMobile ? false : "auto"}
              allowScrollButtonsMobile={!isMobile}
              sx={{
                minHeight: isEmbedded ? 40 : { xs: 40, sm: 44 },
                "& .MuiTab-root": {
                  minHeight: isEmbedded ? 40 : { xs: 40, sm: 44 },
                  px: { xs: 0.75, sm: 2 },
                  fontSize: { xs: "0.76rem", sm: "0.875rem" },
                  textTransform: "none",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <Tab
                value="in-circles"
                label={
                  isMobile
                    ? `Current (${enrichedUserCircles.length})`
                    : `Current circles (${enrichedUserCircles.length})`
                }
              />
              <Tab
                value="can-add"
                label={
                  isMobile
                    ? `All (${filteredAvailableCircles.length})`
                    : `All circles (${filteredAvailableCircles.length})`
                }
              />
            </Tabs>
          </Box>
          {isPageLayout && (
            <Box
              sx={{
                px: { xs: 1.5, sm: isEmbedded ? 2 : 3 },
                pt: isEmbedded ? 1.25 : 2,
                pb: isEmbedded ? 1 : 2,
                backgroundColor: isEmbedded ? "transparent" : "background.paper",
              }}
            >
              <SearchField
                size="small"
                value={
                  desktopTab === "in-circles"
                    ? inCirclesSearchQuery
                    : searchQuery
                }
                onChange={(value) => {
                  if (desktopTab === "in-circles") {
                    setInCirclesSearchQuery(value);
                  } else {
                    setSearchQuery(value);
                  }
                }}
                placeholder={
                  desktopTab === "in-circles"
                    ? "Search circles by name or alias..."
                    : "Search circles by name or description..."
                }
                sx={{ width: "100%" }}
              />
            </Box>
          )}
          {circlesPanel}
        </Paper>
      <VideoPlayerModal
        open={tutorialVideo !== null}
        onClose={() => setTutorialVideo(null)}
        videoPath={tutorialVideo?.videoPath || ""}
        title={tutorialVideo?.title || ""}
      />
      <ActionModal
        open={circlePendingAdd !== null}
        onClose={() => {
          if (!addingCircleId) {
            setCirclePendingAdd(null);
          }
        }}
        disableBackdropClick={!!addingCircleId}
        title="Historical message access"
        subtitle={
          circlePendingAdd
            ? `Adding ${user.name} to ${circlePendingAdd.name}`
            : undefined
        }
        maxWidth="sm"
        footer={
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => !addingCircleId && setCirclePendingAdd(null)}
              disabled={!!addingCircleId}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void confirmAddPendingCircle()}
              disabled={!!addingCircleId}
            >
              {addingCircleId ? "Adding..." : "Add to circle"}
            </Button>
          </Box>
        }
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            backgroundColor: lightTheme.colors.bootstrapPrimaryTint05,
            borderRadius: 2,
            border: `1px solid ${lightTheme.colors.bootstrapPrimaryTint20}`,
          }}
        >
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={legacyAccessOption}
              onChange={(e) =>
                setLegacyAccessOption(
                  e.target.value as "none" | "7days" | "all"
                )
              }
            >
              <FormControlLabel
                value="none"
                control={<Radio color="primary" />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      No access to historical messages
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block" }}
                    >
                      They will only see notices created from the day they join
                      onwards.
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1.5, alignItems: "flex-start" }}
              />
              <FormControlLabel
                value="7days"
                control={<Radio color="primary" />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Allow access to historical messages from last 7 days
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block" }}
                    >
                      They will see notices from the last 7 days before they
                      join, plus all future notices.
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1.5, alignItems: "flex-start" }}
              />
              <FormControlLabel
                value="all"
                control={<Radio color="primary" />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Allow access to all historical messages
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block" }}
                    >
                      They will see all notices from this circle, including
                      messages from before they joined.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start" }}
              />
            </RadioGroup>
          </FormControl>
        </Box>
      </ActionModal>
    </>
    );
  }
);

ManageUserCircles.displayName = "ManageUserCircles";
